import { observable, action, makeObservable } from 'mobx'
import { types as t } from '@invisr/mobx-state-tree'
import errorToast from '../../utils/error-toast'
import crudActions from './crud-actions'
import fetch from '../../utils/fetch'

interface Signer {
  email: string,
  name: string,
  roleName: string,
  data?: Array<any>
}

export const callDocusign = async (actionName, method, config, body = void 0) => {
  if (!config) return

  const res = await fetch(`/api/datasource/${config.dbKey}/${actionName}`, {
    method: method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  })

  let result
  try {
    result = await res.json()
  } catch (err) {
    result = await res.text()
  }

  if (!res.ok) {
    let error
    try {
      error = JSON.parse(result.error)
    } catch (e) {
      error = result.error
    }
    throw error || result
  }

  return result
}

class DocusignProvider {
  isLoading = false
  config
  data = {}

  constructor (config = void 0) {
    makeObservable(this, {
      isLoading: observable,
      data: observable,
      setIsLoading: action,
      setIsLoadingOff: action,
      query: action
    })

    this.config = config
  }

  setIsLoading = () => {
    this.isLoading = true
  }

  setIsLoadingOff = () => {
    this.isLoading = false
  }

  query = context => {
    return Promise.resolve()
  }

  getEnvelope = async envelopeId => {
    try {
      if (envelopeId) {
        return await callDocusign('getEnvelope/' + envelopeId, 'GET', this.config)
      }
    } catch (error) {
      errorToast('Docusign: ', error)
    }
  }

  listTemplates = async () => {
    const templatesResult = await callDocusign('listTemplates', 'GET', this.config)
    return templatesResult.envelopeTemplates
  }

  sendFromTemplate = async (signers: Array<Signer>, subject?: string, templateId?: string) => {
    try {
      return callDocusign('sendFromTemplate', 'POST', this.config, { signers, subject, templateId })
    } catch (error) {
      errorToast('Error during send', error)
      console.log(error)
    }
  }

  getEmbeddedSigningUrl = async (signer: Signer, returnUrl: string, templateId?: string) => {
    try {
      if (Array.isArray(signer)) {
        if (signer.length !== 1) throw new Error('Only one signer can be defined for embedded signing.')
        signer = signer[0]
      }
      return callDocusign('embeddedSigning', 'POST', this.config, { signer, returnUrl, templateId })
    } catch (error) {
      errorToast('Error during send', error)
      console.log(error)
    }
  }
}

export class DocusignMetadata {
  templates = null
  credentials = null
  config = null

  constructor (credentials, config) {
    makeObservable(this, {
      templates: observable
    })

    if (!this.templates && credentials) {
      this.credentials = credentials
      this.config = config
      const provider = new DocusignProvider(config)
      provider.listTemplates()
        .then(
          action(result => {
            if (!result) throw new Error('Could not connect to Docusign; Check your Docusign credentials.')
            this.templates = result
          })
        )
        .catch(er => errorToast('Docusign error: ', er))
    }
  }
}

let metadata = null
export default t
  .model('Docusign', {
    type: t.optional(t.string, 'Docusign'),
    templateId: t.maybeNull(t.string),
    apiUsername: t.maybeNull(t.string)
  })
  .views(self => ({
    get provider () {
      return DocusignProvider
    },
    get metadata () {
      if (!metadata || metadata.credentials !== (self as any).credentials) {
        metadata = new DocusignMetadata((self as any).credentials, self)
      }
      return metadata
    },
    fieldPath (node) {
      return node.meta && node.meta.fieldPath
    },
    labels () {
      const p = new DocusignProvider()
      const childNodes = Object.keys(p)
        .filter(k => typeof p[k] === 'function')
        .map(f => ({
          key: f,
          label: f,
          secondaryLabel: 'FUNCTION',
          meta: {
            fieldPath: f
          }
        }))
      return {
        key: (self as any).id,
        label: (self as any).name,
        childNodes: [
          {
            key: 'methods',
            label: 'Methods',
            childNodes
          },
          {
            key: 'data',
            label: 'Data',
            childNodes: []
          }
        ]
      }
    }
  }))
  .actions(crudActions)
